import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Save, Upload, Image, Type, RefreshCw, Check, X, 
  FileText, Home, Car, Users, Phone, Settings, Globe, Trash2, ImagePlus,
  Plus, Film, FolderPlus, Search, DollarSign, Handshake, Building2, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { LibraryPickerDialog } from '../components/cms/LibraryPicker';

const API = process.env.REACT_APP_BACKEND_URL;

// Editable Text Field Component
const EditableTextField = ({ label, value, onChange, multiline = false, placeholder = '' }) => {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y"
          data-testid={`field-${label.toLowerCase().replace(/\s+/g, '-')}`}
        />
      ) : (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={`field-${label.toLowerCase().replace(/\s+/g, '-')}`}
        />
      )}
    </div>
  );
};

// Image Upload Field Component
const ImageUploadField = ({ label, value, onChange, category = 'cms' }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    setPreview(value || '');
  }, [value]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    try {
      const res = await fetch(`${API}/api/uploads/image`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      
      if (data.success) {
        const fullUrl = `${API}${data.url}`;
        setPreview(fullUrl);
        onChange(fullUrl);
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      toast.error('Upload error');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <div className="flex items-start gap-4">
        {preview ? (
          <div className="relative group">
            <img 
              src={preview} 
              alt={label} 
              className="w-32 h-24 object-cover rounded-lg border border-gray-200"
            />
            <button
              onClick={handleRemove}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <div className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
            <Image className="h-8 w-8 text-gray-400" />
          </div>
        )}
        <div className="flex-1">
          <label className="cursor-pointer">
            <div className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${uploading ? 'opacity-50' : ''}`}>
              <Upload className="h-4 w-4" />
              {uploading ? 'Uploading...' : 'Upload Image'}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
              data-testid={`upload-${label.toLowerCase().replace(/\s+/g, '-')}`}
            />
          </label>
          <p className="text-xs text-gray-500 mt-1">PNG, JPG, WebP up to 10MB</p>
          <button type="button" onClick={() => setPickerOpen(true)}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700"
            data-testid={`browse-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <ImagePlus className="h-3.5 w-3.5" /> Browse Library
          </button>
          {value && (
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Or paste image URL"
              className="mt-2 text-xs"
            />
          )}
        </div>
      </div>
      <LibraryPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} type="image"
        onSelect={(url) => { setPreview(url); onChange(url); }} />
    </div>
  );
};

// Video Field Component — upload, browse library, inline preview
const VideoUploadField = ({ label, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', 'cms');
    try {
      const res = await fetch(`${API}/api/uploads/video`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) { onChange(`${API}${data.url}`); toast.success('Video uploaded'); }
      else toast.error(data.detail || 'Upload failed');
    } catch { toast.error('Upload error'); }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <div className="flex items-start gap-4">
        {value ? (
          <div className="relative group">
            <video src={value} className="w-40 h-24 object-cover rounded-lg border bg-black" controls preload="metadata" />
            <button onClick={() => onChange('')}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <div className="w-40 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
            <Film className="h-8 w-8 text-gray-400" />
          </div>
        )}
        <div className="flex-1">
          <label className="cursor-pointer">
            <div className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${uploading ? 'opacity-50' : ''}`}>
              <Upload className="h-4 w-4" />{uploading ? 'Uploading…' : 'Upload Video'}
            </div>
            <input type="file" accept="video/*" onChange={handleUpload} disabled={uploading} className="hidden" />
          </label>
          <p className="text-xs text-gray-500 mt-1">MP4, MOV, WebM up to 100MB</p>
          <button type="button" onClick={() => setPickerOpen(true)}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700"
            data-testid={`browse-video-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <Film className="h-3.5 w-3.5" /> Browse Library
          </button>
          {value && <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Or paste video URL" className="mt-2 text-xs" />}
        </div>
      </div>
      <LibraryPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} type="video" onSelect={onChange} />
    </div>
  );
};

// Section Editor Component — renders ALL stored fields (config labels + any extras)
const SectionEditor = ({ pageId, sectionId, sectionName, fields, content, onFieldChange, onDeleteSection, onDeleteField, isCustom, searchTerm }) => {
  const [addingField, setAddingField] = useState(false);
  const [newField, setNewField] = useState({ name: '', type: 'text' });

  // Union of configured fields and whatever exists in the content doc
  const allKeys = [...new Set([
    ...Object.keys(fields || {}),
    ...Object.keys(content || {}).filter(k => !k.startsWith('_')),
  ])];

  const labelFor = (k) => (fields?.[k]?.label) || k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const term = (searchTerm || '').toLowerCase();
  const visibleKeys = term
    ? allKeys.filter(k => labelFor(k).toLowerCase().includes(term) || String(content?.[k] || '').toLowerCase().includes(term))
    : allKeys;
  if (term && visibleKeys.length === 0) return null;

  const addField = () => {
    const key = newField.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!key) return;
    const finalKey = newField.type === 'image' && !/image|logo|photo/.test(key) ? `${key}_image`
      : newField.type === 'video' && !/video/.test(key) ? `${key}_video` : key;
    onFieldChange(sectionId, finalKey, '');
    setNewField({ name: '', type: 'text' });
    setAddingField(false);
  };

  return (
    <Card className="p-6 border-gray-200 mb-4" data-testid={`cms-section-${sectionId}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <FileText className="h-5 w-5 text-red-600" />
          {sectionName}
          {isCustom && <span className="text-[9px] uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">Custom</span>}
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setAddingField(a => !a)}
            className="text-xs font-bold text-gray-500 hover:text-red-600 flex items-center gap-1"
            data-testid={`add-field-${sectionId}`}>
            <Plus className="h-3.5 w-3.5" /> Add Field
          </button>
          <button onClick={() => onDeleteSection(sectionId, sectionName)}
            className="text-xs font-bold text-gray-400 hover:text-red-600 flex items-center gap-1"
            title="Remove this whole section"
            data-testid={`delete-section-${sectionId}`}>
            <Trash2 className="h-3.5 w-3.5" /> Remove Section
          </button>
        </div>
      </div>

      {addingField && (
        <div className="flex items-end gap-2 mb-4 p-3 bg-gray-50 rounded-lg border">
          <div className="flex-1">
            <Label className="text-xs text-gray-500">Field name (e.g. "Weekend Price")</Label>
            <Input value={newField.name} onChange={e => setNewField(f => ({ ...f, name: e.target.value }))} className="h-9" data-testid={`new-field-name-${sectionId}`} />
          </div>
          <select value={newField.type} onChange={e => setNewField(f => ({ ...f, type: e.target.value }))} className="h-9 border rounded-md px-2 text-sm" data-testid={`new-field-type-${sectionId}`}>
            <option value="text">Text</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
          <Button size="sm" onClick={addField} className="bg-red-600 hover:bg-red-700 h-9" data-testid={`new-field-add-${sectionId}`}>Add</Button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {visibleKeys.map((fieldKey) => {
          const currentValue = content?.[fieldKey] ?? '';
          const isVideo = fieldKey.includes('video');
          const isImage = !isVideo && (fieldKey.includes('image') || fieldKey.includes('logo') || fieldKey.includes('favicon') || fieldKey.includes('photo'));
          const isMultiline = fieldKey.includes('description') || fieldKey.includes('desc') || fieldKey.includes('features') || fieldKey.includes('body');
          const extraField = !(fields && fieldKey in fields);

          const removeBtn = extraField && (
            <button onClick={() => onDeleteField(sectionId, fieldKey)}
              className="text-[10px] text-gray-400 hover:text-red-600 flex items-center gap-0.5 mt-1"
              data-testid={`delete-field-${sectionId}-${fieldKey}`}>
              <Trash2 className="h-3 w-3" /> remove field
            </button>
          );

          if (isVideo) {
            return (
              <div key={fieldKey}>
                <VideoUploadField label={labelFor(fieldKey)} value={String(currentValue || '')} onChange={(val) => onFieldChange(sectionId, fieldKey, val)} />
                {removeBtn}
              </div>
            );
          }
          if (isImage) {
            return (
              <div key={fieldKey}>
                <ImageUploadField label={labelFor(fieldKey)} value={String(currentValue || '')} onChange={(val) => onFieldChange(sectionId, fieldKey, val)} category="cms" />
                {removeBtn}
              </div>
            );
          }
          return (
            <div key={fieldKey}>
              <EditableTextField label={labelFor(fieldKey)} value={String(currentValue ?? '')} onChange={(val) => onFieldChange(sectionId, fieldKey, val)} multiline={isMultiline} placeholder={fields?.[fieldKey]?.placeholder || ''} />
              {removeBtn}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// Page configurations for CMS
const PAGE_CONFIGS = {
  home: {
    icon: Home,
    name: 'Home Page',
    sections: {
      hero: {
        name: 'Hero Section',
        fields: {
          title: { label: 'Main Title' },
          subtitle: { label: 'Subtitle' },
          cta_primary: { label: 'Primary Button Text' },
          cta_secondary: { label: 'Secondary Button Text' },
          background_image: { label: 'Background Image' }
        }
      },
      intro: {
        name: 'Introduction',
        fields: {
          heading: { label: 'Heading' },
          description: { label: 'Description' }
        }
      },
      driven_card: {
        name: 'DRIVEN Card',
        fields: {
          title: { label: 'Card Title' },
          description: { label: 'Description' },
          image: { label: 'Card Image' },
          cta: { label: 'Button Text' }
        }
      },
      driving_card: {
        name: 'DRIVING Card',
        fields: {
          title: { label: 'Card Title' },
          description: { label: 'Description' },
          image: { label: 'Card Image' },
          cta: { label: 'Button Text' }
        }
      },
      testimonials: {
        name: 'Testimonials',
        fields: {
          heading: { label: 'Section Heading' }
        }
      },
      cta: {
        name: 'Call to Action',
        fields: {
          title: { label: 'CTA Title' },
          description: { label: 'CTA Description' }
        }
      }
    }
  },
  driven: {
    icon: Car,
    name: 'Concierge-Driven',
    sections: {
      hero: {
        name: 'Hero Section',
        fields: {
          title: { label: 'Page Title' },
          subtitle: { label: 'Subtitle' },
          background_image: { label: 'Background Image' }
        }
      },
      services: {
        name: 'Service Categories',
        fields: {
          black_car_title: { label: 'Black Car Service Title' },
          black_car_desc: { label: 'Black Car Description' },
          black_car_image: { label: 'Black Car Image' },
          group_title: { label: 'Group Transport Title' },
          group_desc: { label: 'Group Transport Description' },
          group_image: { label: 'Group Transport Image' },
          exotic_title: { label: 'Exotic Service Title' },
          exotic_desc: { label: 'Exotic Service Description' },
          exotic_image: { label: 'Exotic Service Image' }
        }
      },
      additional: {
        name: 'Additional Services',
        fields: {
          airport_title: { label: 'Airport Title' },
          airport_desc: { label: 'Airport Description' },
          wedding_title: { label: 'Wedding Title' },
          wedding_desc: { label: 'Wedding Description' },
          vip_title: { label: 'VIP Title' },
          vip_desc: { label: 'VIP Description' },
          production_title: { label: 'Production Title' },
          production_desc: { label: 'Production Description' }
        }
      },
      cta: {
        name: 'Call to Action',
        fields: {
          title: { label: 'CTA Title' },
          description: { label: 'CTA Description' }
        }
      }
    }
  },
  driving: {
    icon: Car,
    name: 'Consultations',
    sections: {
      hero: {
        name: 'Hero Section',
        fields: {
          title: { label: 'Page Title' },
          subtitle: { label: 'Subtitle' },
          background_image: { label: 'Background Image' }
        }
      },
      who: {
        name: 'Who We Help',
        fields: {
          buyers_title: { label: 'Buyers Title' },
          buyers_desc: { label: 'Buyers Description' },
          investors_title: { label: 'Investors Title' },
          investors_desc: { label: 'Investors Description' },
          businesses_title: { label: 'Businesses Title' },
          businesses_desc: { label: 'Businesses Description' },
          drivers_title: { label: 'Drivers Title' },
          drivers_desc: { label: 'Drivers Description' }
        }
      },
      services: {
        name: 'Core Services',
        fields: {
          sales_title: { label: 'Sales & Acquisition Title' },
          sales_desc: { label: 'Sales & Acquisition Description' },
          fleet_title: { label: 'Fleet Management Title' },
          fleet_desc: { label: 'Fleet Management Description' },
          consultation_title: { label: '1-on-1 Consultation Title' },
          consultation_desc: { label: '1-on-1 Consultation Description' },
          auction_title: { label: 'Dealer Auction Title' },
          auction_desc: { label: 'Dealer Auction Description' },
          maintenance_title: { label: 'Maintenance Title' },
          maintenance_desc: { label: 'Maintenance Description' }
        }
      },
      cta: {
        name: 'Call to Action',
        fields: {
          title: { label: 'CTA Title' },
          description: { label: 'CTA Description' }
        }
      }
    }
  },
  fleet: {
    icon: Car,
    name: 'Fleet Gallery',
    sections: {
      hero: {
        name: 'Hero Section',
        fields: {
          title: { label: 'Page Title' },
          subtitle: { label: 'Subtitle' }
        }
      }
    }
  },
  membership: {
    icon: Users,
    name: 'Memberships',
    sections: {
      hero: {
        name: 'Hero Section',
        fields: {
          title: { label: 'Page Title' },
          subtitle: { label: 'Subtitle' },
          background_image: { label: 'Background Image' }
        }
      },
      tiers: {
        name: 'Membership Tiers',
        fields: {
          silver_desc: { label: 'Silver Description' },
          gold_desc: { label: 'Gold Description' },
          black_desc: { label: 'Black Description' }
        }
      }
    }
  },
  contact: {
    icon: Phone,
    name: 'Contact',
    sections: {
      hero: {
        name: 'Hero Section',
        fields: {
          title: { label: 'Page Title' },
          subtitle: { label: 'Subtitle' },
          background_image: { label: 'Background Image' }
        }
      },
      info: {
        name: 'Contact Information',
        fields: {
          address: { label: 'Address' },
          phone: { label: 'Phone Number' },
          email: { label: 'Email Address' },
          hours: { label: 'Business Hours' }
        }
      }
    }
  },
  global: {
    icon: Globe,
    name: 'Global Settings',
    sections: {
      brand: {
        name: 'Branding',
        fields: {
          company_name: { label: 'Company Name' },
          tagline: { label: 'Tagline' },
          logo_image: { label: 'Logo Image' },
          favicon: { label: 'Favicon' }
        }
      },
      footer: {
        name: 'Footer',
        fields: {
          copyright: { label: 'Copyright Text' },
          tagline: { label: 'Footer Tagline' },
          social_facebook: { label: 'Facebook URL' },
          social_youtube: { label: 'YouTube URL' },
          social_linkedin: { label: 'LinkedIn URL' },
          social_email: { label: 'Email' }
        }
      }
    }
  },
  pricing: { icon: DollarSign, name: 'Pricing', sections: {} },
  corporate: { icon: Building2, name: 'Corporate', sections: {} },
  partners: { icon: Handshake, name: 'Partners', sections: {} },
  consultation: { icon: ClipboardList, name: 'Consultation', sections: {} },
  consultations: { icon: ClipboardList, name: 'Consultations Page', sections: {} },
  'services.overview': { icon: Car, name: 'Services Overview', sections: {} },
  'services.concierge': { icon: Car, name: 'Concierge Services', sections: {} }
};

// Quick templates for creating a custom section
const SECTION_TEMPLATES = {
  'Text Block': { heading: '', body: '' },
  'Photo + Text': { heading: '', body: '', image: '' },
  'Video Feature': { heading: '', body: '', video: '' },
  'Offer / Price': { heading: '', body: '', price: '', cta_text: 'Book Now', cta_link: '/booking' },
};

const CMSAdmin = () => {
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('home');
  const [activeTab, setActiveTab] = useState('content');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fieldSearch, setFieldSearch] = useState('');
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSection, setNewSection] = useState({ name: '', template: 'Text Block' });

  // Load gallery images
  const loadGallery = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/uploads/list`);
      const data = await res.json();
      setGalleryImages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load gallery:', err);
    }
  }, []);

  // Load content for a page
  const loadPageContent = useCallback(async (pageId) => {
    try {
      const res = await fetch(`${API}/api/cms/pages/${pageId}`);
      const data = await res.json();
      setContent(prev => ({
        ...prev,
        [pageId]: data.sections || {}
      }));
    } catch (err) {
      console.error('Failed to load content:', err);
      toast.error('Failed to load content');
    }
  }, []);

  // Load all pages on mount
  useEffect(() => {
    const loadAllContent = async () => {
      setLoading(true);
      for (const pageId of Object.keys(PAGE_CONFIGS)) {
        await loadPageContent(pageId);
      }
      await loadGallery();
      setLoading(false);
    };
    loadAllContent();
  }, [loadPageContent, loadGallery]);

  // Handle field change
  const handleFieldChange = (sectionId, fieldKey, value) => {
    setContent(prev => ({
      ...prev,
      [activePage]: {
        ...prev[activePage],
        [sectionId]: {
          ...prev[activePage]?.[sectionId],
          [fieldKey]: value
        }
      }
    }));
    setHasChanges(true);
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const pageContent = content[activePage];
      const updates = [];
      
      for (const [sectionId, sectionContent] of Object.entries(pageContent)) {
        for (const [field, value] of Object.entries(sectionContent)) {
          if (field.startsWith('_')) continue;
          updates.push({ section_id: sectionId, field, value });
        }
      }
      
      const res = await fetch(`${API}/api/cms/pages/${activePage}/bulk`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      if (res.ok) {
        toast.success('Content saved successfully');
        setHasChanges(false);
      } else {
        toast.error('Failed to save content');
      }
    } catch (err) {
      toast.error('Save error');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Reset to default
  const handleReset = async () => {
    if (!window.confirm('Reset this page to default content? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API}/api/cms/pages/${activePage}/reset`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        await loadPageContent(activePage);
        toast.success('Page reset to default');
        setHasChanges(false);
      }
    } catch (err) {
      toast.error('Reset failed');
    }
  };

  // ── Custom sections ──
  const handleAddSection = async () => {
    const name = newSection.name.trim();
    if (!name) { toast.error('Give the section a name'); return; }
    try {
      const res = await fetch(`${API}/api/cms/pages/${activePage}/sections`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, fields: SECTION_TEMPLATES[newSection.template] || {} }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Create failed');
      toast.success(`Section "${name}" added — fill it in and Save to publish`);
      setAddSectionOpen(false);
      setNewSection({ name: '', template: 'Text Block' });
      await loadPageContent(activePage);
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteSection = async (sectionId, sectionName) => {
    if (!window.confirm(`Remove the whole "${sectionName}" section? (Default sections can be restored with Reset)`)) return;
    try {
      const res = await fetch(`${API}/api/cms/pages/${activePage}/sections/${sectionId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Section removed');
      setContent(prev => {
        const page = { ...prev[activePage] };
        delete page[sectionId];
        return { ...prev, [activePage]: page };
      });
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteField = async (sectionId, field) => {
    try {
      const res = await fetch(`${API}/api/cms/pages/${activePage}/sections/${sectionId}/fields/${field}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok && res.status !== 404) throw new Error('Remove failed');
    } catch (e) { toast.error(e.message); return; }
    setContent(prev => {
      const section = { ...prev[activePage]?.[sectionId] };
      delete section[field];
      return { ...prev, [activePage]: { ...prev[activePage], [sectionId]: section } };
    });
    toast.success('Field removed');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" data-testid="cms-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const currentPageConfig = PAGE_CONFIGS[activePage];
  const PageIcon = currentPageConfig.icon;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="cms-admin">
      {/* Header */}
      <header className="bg-black text-white py-4 shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin')} 
                className="text-gray-300 hover:text-white flex-shrink-0"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold truncate">Content Manager</h1>
                <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Edit text and images across your site</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasChanges && (
                <span className="text-yellow-400 text-xs hidden sm:inline">Unsaved</span>
              )}
              <Button 
                onClick={handleReset}
                variant="outline" 
                size="sm"
                className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500"
                data-testid="reset-btn"
              >
                <RefreshCw className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Reset</span>
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving || !hasChanges}
                size="sm"
                className="bg-red-600 hover:bg-red-700"
                data-testid="save-btn"
              >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <><Save className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Save</span></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Tab Selector: Content vs Photo Gallery */}
        <div className="flex gap-4 mb-6">
          <Button
            variant={activeTab === 'content' ? 'default' : 'outline'}
            className={activeTab === 'content' ? 'bg-red-600 hover:bg-red-700' : ''}
            onClick={() => setActiveTab('content')}
            data-testid="tab-content"
          >
            <Type className="h-4 w-4 mr-2" />Content Editor
          </Button>
          <Button
            variant={activeTab === 'gallery' ? 'default' : 'outline'}
            className={activeTab === 'gallery' ? 'bg-red-600 hover:bg-red-700' : ''}
            onClick={() => setActiveTab('gallery')}
            data-testid="tab-gallery"
          >
            <ImagePlus className="h-4 w-4 mr-2" />Photo Gallery
          </Button>
        </div>

        {activeTab === 'content' ? (
          <>
            {/* Page Selector */}
            <div className="flex flex-wrap gap-2 mb-6">
              {Object.entries(PAGE_CONFIGS).map(([pageId, config]) => {
                const Icon = config.icon;
                return (
                  <Button
                    key={pageId}
                    variant={activePage === pageId ? 'default' : 'outline'}
                    className={activePage === pageId ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => setActivePage(pageId)}
                    data-testid={`page-btn-${pageId}`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {config.name}
                  </Button>
                );
              })}
            </div>

            {/* Page Content Editor */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <PageIcon className="h-6 w-6 text-red-600" />
                Editing: {currentPageConfig.name}
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input value={fieldSearch} onChange={e => setFieldSearch(e.target.value)}
                    placeholder="Search text on this page…" className="pl-9 h-9 w-56" data-testid="cms-field-search" />
                </div>
                <Button size="sm" variant="outline" onClick={() => setAddSectionOpen(o => !o)} className="h-9" data-testid="cms-add-section-btn">
                  <FolderPlus className="h-4 w-4 mr-1.5" /> Add Section
                </Button>
              </div>
            </div>

            {/* Add-section builder */}
            {addSectionOpen && (
              <Card className="p-5 mb-4 border-red-200 bg-red-50" data-testid="cms-add-section-form">
                <p className="text-sm font-bold text-gray-800 mb-3">New section on “{currentPageConfig.name}” — shows on the public page automatically</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <Label className="text-xs text-gray-500">Section name (heading shown on site)</Label>
                    <Input value={newSection.name} onChange={e => setNewSection(s => ({ ...s, name: e.target.value }))}
                      placeholder='e.g. "Summer Wedding Special — $150/hr"' className="h-9 bg-white" data-testid="cms-new-section-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Starting layout</Label>
                    <select value={newSection.template} onChange={e => setNewSection(s => ({ ...s, template: e.target.value }))}
                      className="h-9 border rounded-md px-2 text-sm block bg-white" data-testid="cms-new-section-template">
                      {Object.keys(SECTION_TEMPLATES).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <Button size="sm" onClick={handleAddSection} className="bg-red-600 hover:bg-red-700 h-9" data-testid="cms-new-section-create">
                    <Plus className="h-4 w-4 mr-1" /> Create Section
                  </Button>
                </div>
              </Card>
            )}

            {/* Sections — configured + anything custom stored in the content doc */}
            {(() => {
              const configSections = currentPageConfig.sections || {};
              const contentSections = content[activePage] || {};
              const orderedIds = [...Object.keys(configSections), ...Object.keys(contentSections).filter(id => !(id in configSections))];
              return orderedIds.map((sectionId) => {
                const cfg = configSections[sectionId];
                const secContent = contentSections[sectionId] || {};
                const isCustom = !!secContent._custom;
                const name = cfg?.name || secContent._name || sectionId.replace(/^custom_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                return (
                  <SectionEditor
                    key={sectionId}
                    pageId={activePage}
                    sectionId={sectionId}
                    sectionName={name}
                    fields={cfg?.fields || {}}
                    content={secContent}
                    onFieldChange={handleFieldChange}
                    onDeleteSection={handleDeleteSection}
                    onDeleteField={handleDeleteField}
                    isCustom={isCustom}
                    searchTerm={fieldSearch}
                  />
                );
              });
            })()}
          </>
        ) : (
          /* Photo Gallery Tab */
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-2">
                <ImagePlus className="h-6 w-6 text-red-600" />
                Photo Gallery
              </h2>
              <p className="text-gray-600 text-sm">Upload and manage images for your site. Drag and drop or click to upload.</p>
            </div>

            {/* Upload Area */}
            <Card className="p-8 border-2 border-dashed border-gray-300 hover:border-red-400 transition-colors mb-8 text-center"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const files = e.dataTransfer.files;
                if (!files.length) return;
                setUploading(true);
                for (const file of files) {
                  if (!file.type.startsWith('image/')) continue;
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('category', 'gallery');
                  try {
                    await fetch(`${API}/api/uploads/image`, { method: 'POST', body: formData });
                  } catch (err) { console.error(err); }
                }
                await loadGallery();
                setUploading(false);
                toast.success('Images uploaded!');
              }}
              data-testid="gallery-dropzone"
            >
              <ImagePlus className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">{uploading ? 'Uploading...' : 'Drag & drop images here'}</p>
              <label className="cursor-pointer">
                <Button variant="outline" className="pointer-events-none">
                  <Upload className="h-4 w-4 mr-2" />Or click to browse
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files?.length) return;
                    setUploading(true);
                    for (const file of files) {
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('category', 'gallery');
                      try {
                        await fetch(`${API}/api/uploads/image`, { method: 'POST', body: formData });
                      } catch (err) { console.error(err); }
                    }
                    await loadGallery();
                    setUploading(false);
                    toast.success('Images uploaded!');
                  }}
                  data-testid="gallery-file-input"
                />
              </label>
              <p className="text-xs text-gray-400 mt-2">JPEG, PNG, WebP, GIF up to 10MB each</p>
            </Card>

            {/* Gallery Grid */}
            {galleryImages.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {galleryImages.map((img) => (
                  <Card key={img.file_id} className="group relative overflow-hidden" data-testid={`gallery-img-${img.file_id}`}>
                    <div className="aspect-square bg-gray-100">
                      <img
                        src={`${API}${img.url}`}
                        alt={img.filename}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-gray-600 truncate">{img.filename}</p>
                      <div className="flex items-center justify-between mt-1">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${API}${img.url}`);
                            toast.success('Image URL copied!');
                          }}
                          className="text-xs text-blue-600 hover:underline"
                          data-testid={`copy-url-${img.file_id}`}
                        >
                          Copy URL
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Delete this image?')) return;
                            const r = await fetch(`${API}/api/uploads/files/${img.file_id}`, { method: 'DELETE', credentials: 'include' });
                            if (!r.ok) { toast.error(r.status === 401 ? 'Session expired — log in again' : 'Delete failed'); return; }
                            await loadGallery();
                            toast.success('Image deleted');
                          }}
                          className="text-xs text-red-600 hover:underline"
                          data-testid={`delete-img-${img.file_id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Image className="h-16 w-16 mx-auto mb-3 opacity-50" />
                <p>No images uploaded yet. Upload your first image above.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CMSAdmin;
